/**
 * FinanceCalculator — Carvana-quality standalone financing widget.
 *
 * Used on:
 *   • The vehicle detail page (with a specific vehicle preloaded).
 *   • The `/sell-trade` and Home pages (as an "any-price" tool).
 *
 * Design pillars
 * --------------
 * • Prominent monthly-payment readout (font-display, 4-5xl) drives the
 *   entire card — every other control is arranged to make that number update.
 * • Sliders + pill buttons everywhere; no bare number inputs on primary flows.
 * • Deep-navy panel header with gold accent number matches Belo brand tokens
 *   (navy #0f172a / gold #D4A537). Green accent stays banished to result CTA
 *   badges only — no muddy purple/violet gradients.
 * • Rate disclaimer is inline (not hidden) — "Sample rates. Actual APR
 *   depends on credit approval, term, and lender."
 *
 * ``variant``:
 *   • ``inline`` (default): standalone card, meant for pages that want to
 *     drop the widget inline in a hero/CTA slot. Wraps in a bordered card
 *     with padding.
 *   • ``embedded``: renders without an outer card border. Use inside another
 *     card (e.g. the vehicle detail page rail) where the parent already
 *     provides chrome.
 *
 * ``vehicle`` prop is optional: when passed we lock the vehicle price + wire
 * quote persistence (POST /api/finance/quote). Without a vehicle the widget
 * runs in "any-price" mode — user drags the price slider directly, no
 * backend hit, so the calculator is instant on landing pages.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banknote, DollarSign, Info, Lock, Search, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTools } from "@/context/ToolsContext";

const TERM_OPTIONS = [36, 48, 60, 72, 84];
const CREDIT_TIERS = [
  { value: "excellent",  label: "Excellent",  helper: "720+",     apr: 590,  ordinal: 1 },
  { value: "good",       label: "Good",       helper: "660–719",  apr: 890,  ordinal: 2 },
  { value: "fair",       label: "Fair",       helper: "600–659",  apr: 1290, ordinal: 3 },
  { value: "poor",       label: "Rebuilding", helper: "<600",     apr: 1700, ordinal: 4 },
];
// API contract uses band names "excellent|good|fair|poor" so the widget's
// "Rebuilding" label maps to backend "poor".
const BAND_BY_VALUE = Object.fromEntries(CREDIT_TIERS.map((t) => [t.value, t]));
const APR_BY_BAND = Object.fromEntries(CREDIT_TIERS.map((t) => [t.value, t.apr]));

const fmtUSD = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);
const fmtUSDDec = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);

/** Local amortization mirror so the "any-price" preview stays instant even
 *  before the API responds — same formula as backend. Cents in, cents out. */
const localMonthly = (principalCents, aprBps, months) => {
  if (principalCents <= 0 || months <= 0) return 0;
  if (aprBps === 0) return Math.ceil(principalCents / months);
  const c = aprBps / 10000 / 12;
  const f = Math.pow(1 + c, months);
  return Math.round((principalCents * (c * f)) / (f - 1));
};

const FinanceCalculator = ({ vehicle = null, variant = "inline", showBrowseCta = false }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { activeTradeIn, clearTradeIn, tradeInCreditCents } = useTools();

  const vehiclePrice = vehicle?.price ? Math.round(vehicle.price) : 20000;
  const [price, setPrice] = useState(vehiclePrice);
  const [down, setDown] = useState(() => Math.max(500, Math.min(10000, Math.round(vehiclePrice * 0.10 / 100) * 100)));
  const [term, setTerm] = useState(60);
  const [band, setBand] = useState("good");

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preQualOpen, setPreQualOpen] = useState(false);

  // Reset when vehicle price changes
  useEffect(() => {
    if (vehicle?.price) {
      setPrice(Math.round(vehicle.price));
      setDown(Math.max(500, Math.min(10000, Math.round(vehicle.price * 0.10 / 100) * 100)));
    }
  }, [vehicle?.id, vehicle?.price]);

  // Live-computed monthly (local mirror). If a vehicle is loaded we also
  // POST to the backend so the quote gets persisted for BDC follow-up.
  const localMonthlyCents = useMemo(() => {
    const tradeCents = vehicle ? (tradeInCreditCents || 0) : 0;
    const principal = Math.max(0, price * 100 - down * 100 - tradeCents);
    return localMonthly(principal, APR_BY_BAND[band], term);
  }, [price, down, band, term, tradeInCreditCents, vehicle]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (!vehicle?.id) { setQuote(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const body = {
          vehicle_id: vehicle.id,
          down_payment_cents: Math.max(0, Math.round(down * 100)),
          loan_term_months: term,
          credit_band: band,
          trade_in_credit_cents: tradeInCreditCents || 0,
          trade_in_estimate_id: activeTradeIn?.estimate_id || null,
        };
        const { data } = await api.post("/finance/quote", body);
        setQuote(data);
      } catch (e) {
        if (e.response?.status === 429) toast.error("Slow down a bit — try again in a moment.");
      } finally { setLoading(false); }
    }, 320);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.id, down, term, band, tradeInCreditCents, activeTradeIn?.estimate_id]);

  const monthlyCents = quote?.monthly_payment_cents ?? localMonthlyCents;
  const aprBpsUsed = quote?.apr_bps_used ?? APR_BY_BAND[band];
  const aprPct = (aprBpsUsed / 100).toFixed(2);

  const saveQuote = async () => {
    if (!quote?.quote_id) return;
    if (!isAuthenticated) { toast.error("Please sign in to save quotes."); return; }
    try {
      await api.post("/finance/quote/save", { quote_id: quote.quote_id });
      toast.success("Quote saved to your account.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const preQualify = async ({ name, email, phone, message }) => {
    if (!quote?.quote_id) return;
    try {
      await api.post("/finance/quote/convert-to-lead", {
        quote_id: quote.quote_id, name, email, phone, message,
      });
      toast.success("A BeloMotors specialist will follow up shortly.");
      setPreQualOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const seeMatchesInventory = () => {
    // Rough inverse-amortization: acceptable_max_price ≈ (monthly × term) + down
    // and bump slightly for interest tolerance.
    const monthlyDollars = Math.round(monthlyCents / 100);
    const maxPrice = Math.round(monthlyDollars * term * 1.02) + down;
    navigate(`/inventory?price_max=${maxPrice}`);
  };

  const outerClass = variant === "embedded"
    ? "bg-white"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden";

  return (
    <section className={outerClass} data-testid="finance-calculator">
      {/* Header — deep navy panel with gold monthly-payment number */}
      <div className="px-5 sm:px-7 py-5 sm:py-6 bg-slate-950 text-white rounded-t-2xl" data-testid="finance-calculator-header">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-gold text-slate-950 flex items-center justify-center">
              <Banknote size={17} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-white/50 font-semibold">Finance</p>
              <p className="mt-0.5 font-display text-base sm:text-lg font-semibold">Estimate your monthly payment</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold">Est. Monthly</p>
            <p className="mt-0.5 flex items-baseline justify-end gap-1">
              <span className="font-display text-3xl sm:text-4xl font-bold text-gold" data-testid="finance-monthly-amount">
                {fmtUSD(monthlyCents)}
              </span>
              <span className="text-xs text-white/60">/mo</span>
            </p>
            <p className="text-[10px] text-white/50 mt-0.5">APR {aprPct}% · {term}mo{loading ? " · ↻" : ""}</p>
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-7 space-y-5">
        {activeTradeIn && vehicle && (
          <div
            className="flex items-center gap-2 text-xs bg-gold/10 border border-gold/30 rounded-lg px-3 py-2 text-slate-800"
            data-testid="finance-tradein-chip"
          >
            <span className="font-semibold">
              Trade-in credit applied: {fmtUSD(activeTradeIn.estimate_mid_cents)}
            </span>
            <span className="text-slate-500 flex-1 truncate">
              {activeTradeIn.year} {activeTradeIn.make} {activeTradeIn.model}
            </span>
            <button
              onClick={clearTradeIn}
              className="text-slate-500 hover:text-slate-800"
              data-testid="finance-tradein-chip-remove"
              title="Remove trade-in credit"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Vehicle price — hidden when a specific vehicle is loaded */}
        {!vehicle && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Vehicle price</Label>
              <div className="flex items-center gap-1">
                <DollarSign size={11} className="text-slate-400" />
                <Input
                  type="number" min={1000} max={200000} step={500}
                  value={price}
                  onChange={(e) => setPrice(Math.max(1000, Number(e.target.value || 0)))}
                  className="h-8 w-28 text-right font-mono text-sm"
                  data-testid="finance-price-input"
                />
              </div>
            </div>
            <Slider
              min={5000} max={80000} step={500}
              value={[Math.min(80000, price)]}
              onValueChange={([v]) => setPrice(v)}
              data-testid="finance-price-slider"
            />
            <div className="flex justify-between mt-1 text-[10px] text-slate-400 font-mono">
              <span>$5,000</span><span>$80,000</span>
            </div>
          </div>
        )}

        {/* Down payment */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Down payment</Label>
            <div className="flex items-center gap-1">
              <DollarSign size={11} className="text-slate-400" />
              <Input
                type="number" min={0} max={100000} step={100}
                value={down}
                onChange={(e) => setDown(Math.max(0, Math.min(100000, Number(e.target.value || 0))))}
                className="h-8 w-28 text-right font-mono text-sm"
                data-testid="finance-down-input"
              />
            </div>
          </div>
          <Slider
            min={0}
            max={Math.max(10000, Math.min(50000, Math.round(price * 0.5)))}
            step={100}
            value={[Math.min(down, 50000)]}
            onValueChange={([v]) => setDown(v)}
            data-testid="finance-down-slider"
          />
          <div className="flex justify-between mt-1 text-[10px] text-slate-400 font-mono">
            <span>$0</span><span>{Math.round((price * 0.5) / 1000)}k+</span>
          </div>
        </div>

        {/* Loan term pills */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600 mb-2 block">Loan term</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="finance-term-group">
            {TERM_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTerm(t)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  t === term
                    ? "bg-slate-950 text-white shadow-md scale-105"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                data-testid={`finance-term-${t}`}
              >
                {t} mo
              </button>
            ))}
          </div>
        </div>

        {/* Credit tier — 2x2 grid on mobile, 4-col on desktop */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600 mb-2 block">Credit tier</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="finance-credit-group">
            {CREDIT_TIERS.map((c) => {
              const active = c.value === band;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setBand(c.value)}
                  className={`px-3 py-2.5 rounded-xl text-left transition-all border-2 ${
                    active
                      ? "border-slate-950 bg-slate-950 text-white shadow-md"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  }`}
                  data-testid={`finance-credit-${c.value}`}
                >
                  <span className="block text-xs font-semibold">{c.label}</span>
                  <span className={`block text-[10px] mt-0.5 font-mono ${active ? "text-white/70" : "text-slate-500"}`}>
                    {c.helper} · {(c.apr / 100).toFixed(1)}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Breakdown row */}
        <div className="grid grid-cols-3 gap-3 rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="finance-breakdown">
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">Principal</p>
            <p className="mt-0.5 font-mono text-sm font-semibold text-slate-900">
              {fmtUSDDec(quote?.principal_cents ?? Math.max(0, price * 100 - down * 100 - (vehicle ? tradeInCreditCents : 0)))}
            </p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">Interest</p>
            <p className="mt-0.5 font-mono text-sm font-semibold text-slate-900">
              {fmtUSDDec(quote?.total_interest_cents ?? (monthlyCents * term - Math.max(0, price * 100 - down * 100)))}
            </p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">Total</p>
            <p className="mt-0.5 font-mono text-sm font-semibold text-slate-900">
              {fmtUSDDec(quote?.total_cost_cents ?? monthlyCents * term)}
            </p>
          </div>
        </div>

        {/* CTAs + disclaimer */}
        <div className="flex flex-wrap gap-2">
          {vehicle ? (
            <>
              <Button
                variant="outline"
                onClick={saveQuote}
                disabled={!quote?.quote_id || !isAuthenticated}
                title={!isAuthenticated ? "Sign in to save" : ""}
                className="flex-1 sm:flex-none"
                data-testid="finance-save-btn"
              >
                {isAuthenticated ? "Save this quote" : (<><Lock size={12} /> Sign in to save</>)}
              </Button>
              <Button
                className="bg-gold hover:bg-gold-hover text-slate-950 font-semibold flex-1"
                disabled={!quote?.quote_id}
                onClick={() => setPreQualOpen(true)}
                data-testid="finance-prequal-btn"
              >
                Get pre-qualified
              </Button>
            </>
          ) : (
            <Button
              className="bg-slate-950 hover:bg-slate-800 text-white font-semibold flex-1"
              onClick={seeMatchesInventory}
              data-testid="finance-see-matches-btn"
            >
              <Search size={14} /> See vehicles at {fmtUSD(monthlyCents)}/mo
            </Button>
          )}
          {showBrowseCta && (
            <Button variant="outline" onClick={() => navigate("/inventory")} data-testid="finance-browse-btn">
              Browse inventory
            </Button>
          )}
        </div>

        <p className="text-[10px] text-slate-500 flex items-start gap-1.5" data-testid="finance-disclaimer">
          <Info size={10} className="shrink-0 mt-0.5 text-slate-400" />
          Sample rates. Actual APR depends on credit approval, term, and lender.
        </p>
      </div>

      {vehicle && (
        <PreQualDialog
          open={preQualOpen}
          onOpenChange={setPreQualOpen}
          user={user}
          vehicle={vehicle}
          quote={quote}
          onSubmit={preQualify}
        />
      )}
    </section>
  );
};

const PreQualDialog = ({ open, onOpenChange, user, vehicle, quote, onSubmit }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) { setName(user.full_name || ""); setEmail(user.email || ""); setPhone(user.phone || ""); }
  }, [user, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await onSubmit({ name, email, phone, message: msg }); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="finance-prequal-dialog">
        <DialogHeader>
          <DialogTitle>Get pre-qualified</DialogTitle>
          <DialogDescription>
            A BeloMotors financing specialist will follow up about your quote on the {vehicle?.year} {vehicle?.make} {vehicle?.model}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          {quote?.monthly_payment_cents ? (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-900">
              <strong>{fmtUSD(quote.monthly_payment_cents)}/mo</strong> · {quote.loan_term_months}mo · APR {(quote.apr_bps_used/100).toFixed(2)}%
            </div>
          ) : null}
          <div><Label htmlFor="prequal-name">Your name</Label>
            <Input id="prequal-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="finance-prequal-name-input" /></div>
          <div><Label htmlFor="prequal-email">Email</Label>
            <Input id="prequal-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="finance-prequal-email-input" /></div>
          <div><Label htmlFor="prequal-phone">Phone</Label>
            <Input id="prequal-phone" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="finance-prequal-phone-input" /></div>
          <div><Label htmlFor="prequal-msg">Notes (optional)</Label>
            <Input id="prequal-msg" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Best time to reach me…" data-testid="finance-prequal-msg-input" /></div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-gold hover:bg-gold-hover text-slate-950" disabled={submitting} data-testid="finance-prequal-submit-btn">
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FinanceCalculator;
